import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ServerDataGrid } from '../../shared/data/ServerDataGrid';
import type { PageResult } from '../../shared/data/types';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { toPageError } from '../partners/PartnerListPage';
import { adminUsersRepositoryToken, defaultAdminUserListQuery } from './repository';
import type {
  AdminUserFilters,
  AdminUserListQuery,
  AdminUserRole,
  AdminUserSummary,
} from './types';

export function AdminUserListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(adminUsersRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(defaultAdminUserListQuery);
  const [refresh, setRefresh] = useState(0);
  const [inviteOpen, setInviteOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.listUsers(query, signal),
    [query, refresh, repository],
    (page) => page.items.length === 0,
  );
  const canManage =
    auth.status === 'authenticated' &&
    auth.principal.permissions.includes('BACKOFFICE_USER_MANAGE');
  const columns = useMemo<readonly GridColDef<AdminUserSummary>[]>(
    () => [
      {
        field: 'username',
        headerName: t('adminUsers.fields.username'),
        minWidth: 180,
        renderCell: ({ row }) => (
          <Link to={`/admin-users/${row.id}`}>{row.username ?? t('common.notAvailable')}</Link>
        ),
      },
      {
        field: 'displayName',
        headerName: t('adminUsers.fields.displayName'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'email',
        headerName: t('adminUsers.fields.email'),
        minWidth: 220,
        sortable: false,
        valueFormatter: (value: string | null) => value ?? t('common.notAvailable'),
      },
      { field: 'role', headerName: t('adminUsers.fields.role'), minWidth: 180 },
      {
        field: 'status',
        headerName: t('adminUsers.fields.status'),
        minWidth: 120,
        renderCell: ({ row }) => <AdminUserStatusChip status={row.status} />,
      },
      {
        field: 'partnerId',
        headerName: t('adminUsers.fields.partnerId'),
        minWidth: 180,
        sortable: false,
        valueFormatter: (value: string | null) => value ?? t('common.notAvailable'),
      },
      {
        field: 'agentId',
        headerName: t('adminUsers.fields.agentId'),
        minWidth: 180,
        sortable: false,
        valueFormatter: (value: string | null) => value ?? t('common.notAvailable'),
      },
      {
        field: 'permissionProfile',
        headerName: t('adminUsers.fields.profile'),
        minWidth: 180,
        sortable: false,
        valueGetter: (_value, row) => row.permissionProfile?.name ?? t('common.notAvailable'),
      },
      {
        field: 'lastLoginAt',
        headerName: t('adminUsers.fields.lastLogin'),
        minWidth: 190,
        sortable: false,
        valueFormatter: (value: string | null) =>
          value === null
            ? t('common.notAvailable')
            : formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
      {
        field: 'updatedAt',
        headerName: t('adminUsers.fields.updatedAt'),
        minWidth: 190,
        valueFormatter: (value: string) => formatTimestamp(value, 'Asia/Bangkok', language).display,
      },
    ],
    [language, t],
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <>
            <DataSourceBadge source={dataSource} />
            {canManage ? (
              <Button onClick={() => setInviteOpen(true)} variant="contained">
                {t('adminUsers.actions.invite')}
              </Button>
            ) : null}
            <Button component={Link} to="/permission-profiles" variant="outlined">
              {t('adminUsers.profiles.title')}
            </Button>
          </>
        }
        subtitle={t('adminUsers.subtitle')}
        title={t('adminUsers.title')}
      />
      <AdminUserFiltersForm
        onApply={(filters) => setQuery((current) => ({ ...current, page: 1, filters }))}
        value={query.filters}
      />
      <PageState
        emptyMessage={t('adminUsers.empty')}
        error={toPageError(resource.error, t('adminUsers.errors.load'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <AdminUsersGrid
            columns={columns}
            onQueryChange={setQuery}
            page={resource.data}
            query={query}
          />
        )}
      </PageState>
      <InviteDialog
        onClose={() => setInviteOpen(false)}
        onSaved={() => setRefresh((value) => value + 1)}
        open={inviteOpen}
      />
    </Box>
  );
}

function AdminUsersGrid({
  columns,
  onQueryChange,
  page,
  query,
}: {
  readonly columns: readonly GridColDef<AdminUserSummary>[];
  readonly onQueryChange: (query: AdminUserListQuery) => void;
  readonly page: PageResult<AdminUserSummary>;
  readonly query: AdminUserListQuery;
}) {
  const { t } = useTranslation();
  const gridQuery = { ...query, page: query.page - 1 };
  return (
    <Box
      aria-label={t('adminUsers.gridLabel')}
      role="region"
      sx={{ maxWidth: '100%', overflowX: 'auto' }}
      tabIndex={0}
    >
      <ServerDataGrid
        columns={columns}
        disableRowSelectionOnClick
        onQueryChange={(next) => onQueryChange({ ...next, page: next.page + 1 })}
        page={page}
        query={gridQuery}
        sx={{ minWidth: 1680 }}
      />
    </Box>
  );
}

function AdminUserFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: AdminUserFilters) => void;
  readonly value: AdminUserFilters;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<AdminUserFilters>({ defaultValues: value });
  return (
    <ListFilterPanel
      ariaLabel={t('filterPanel.label', { page: t('adminUsers.title') })}
      onClear={() => {
        reset(defaultAdminUserListQuery.filters);
        onApply(defaultAdminUserListQuery.filters);
      }}
      onSubmit={handleSubmit((filters) =>
        onApply({
          ...filters,
          partnerId: filters.partnerId.trim(),
          agentId: filters.agentId.trim(),
          permissionProfileId: filters.permissionProfileId.trim(),
          username: filters.username.trim(),
          email: filters.email.trim(),
        }),
      )}
      primaryFields={
        <>
          <TextField
            label={t('adminUsers.fields.username')}
            size="small"
            {...register('username')}
          />
          <TextField label={t('adminUsers.fields.email')} size="small" {...register('email')} />
          <TextField
            defaultValue=""
            label={t('adminUsers.fields.role')}
            select
            size="small"
            {...register('role')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="SUPER_ADMIN">SUPER_ADMIN</MenuItem>
            <MenuItem value="AGENT_SUPER_USER">AGENT_SUPER_USER</MenuItem>
            <MenuItem value="AGENT_ADMIN_USER">AGENT_ADMIN_USER</MenuItem>
          </TextField>
          <TextField
            defaultValue=""
            label={t('adminUsers.fields.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {['INVITED', 'ACTIVE', 'SUSPENDED', 'CLOSED'].map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </>
      }
    />
  );
}

interface InviteValues {
  readonly subject: string;
  readonly username: string;
  readonly displayName: string;
  readonly email: string;
  readonly role: AdminUserRole;
  readonly partnerId: string;
  readonly agentId: string;
  readonly permissionProfileId: string;
  readonly reason: string;
}
function InviteDialog({
  onClose,
  onSaved,
  open,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
}) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<InviteValues>({
    defaultValues: {
      subject: '',
      username: '',
      displayName: '',
      email: '',
      role: 'AGENT_ADMIN_USER',
      partnerId: '',
      agentId: '',
      permissionProfileId: '',
      reason: '',
    },
  });
  const role = watch('role');
  const required = {
    required: t('adminUsers.validation.required'),
    validate: (value: string) => value.trim().length > 0 || t('adminUsers.validation.required'),
  };
  const close = () => {
    if (!guard.current) {
      reset();
      setError(null);
      onClose();
    }
  };
  const submit = handleSubmit(async (value) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const access =
        value.role === 'SUPER_ADMIN'
          ? { role: 'SUPER_ADMIN' as const }
          : value.role === 'AGENT_SUPER_USER'
            ? {
                role: 'AGENT_SUPER_USER' as const,
                partnerId: value.partnerId.trim(),
                agentId: value.agentId.trim(),
              }
            : {
                role: 'AGENT_ADMIN_USER' as const,
                partnerId: value.partnerId.trim(),
                agentId: value.agentId.trim(),
                permissionProfileId: value.permissionProfileId.trim(),
              };
      await repository.inviteUser({
        subject: value.subject.trim(),
        username: value.username.trim(),
        displayName: value.displayName.trim(),
        email: value.email.trim() || null,
        access,
        reason: value.reason.trim(),
      });
      onSaved();
      reset();
      onClose();
    } catch (cause) {
      setError(toDialogError(cause, t('adminUsers.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth maxWidth="sm" onClose={close} open={open}>
      <DialogTitle>{t('adminUsers.actions.invite')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="invite-admin-user-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            error={!!errors.subject}
            helperText={errors.subject?.message}
            label={t('adminUsers.fields.subject')}
            required
            {...register('subject', required)}
          />
          <TextField
            error={!!errors.username}
            helperText={errors.username?.message}
            label={t('adminUsers.fields.username')}
            required
            {...register('username', required)}
          />
          <TextField
            error={!!errors.displayName}
            helperText={errors.displayName?.message}
            label={t('adminUsers.fields.displayName')}
            required
            {...register('displayName', required)}
          />
          <TextField label={t('adminUsers.fields.email')} type="email" {...register('email')} />
          <TextField
            defaultValue="AGENT_ADMIN_USER"
            label={t('adminUsers.fields.role')}
            select
            {...register('role')}
          >
            <MenuItem value="SUPER_ADMIN">SUPER_ADMIN</MenuItem>
            <MenuItem value="AGENT_SUPER_USER">AGENT_SUPER_USER</MenuItem>
            <MenuItem value="AGENT_ADMIN_USER">AGENT_ADMIN_USER</MenuItem>
          </TextField>
          {role === 'SUPER_ADMIN' ? null : (
            <>
              <TextField
                label={t('adminUsers.fields.partnerId')}
                required
                {...register('partnerId', required)}
              />
              <TextField
                label={t('adminUsers.fields.agentId')}
                required
                {...register('agentId', required)}
              />
            </>
          )}
          {role === 'AGENT_ADMIN_USER' ? (
            <TextField
              label={t('adminUsers.fields.profileId')}
              required
              {...register('permissionProfileId', required)}
            />
          ) : null}
          <TextField
            error={!!errors.reason}
            helperText={errors.reason?.message}
            label={t('adminUsers.fields.reason')}
            multiline
            required
            {...register('reason', required)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="invite-admin-user-form" loading={busy} type="submit" variant="contained">
          {t('adminUsers.actions.invite')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AdminUserStatusChip({ status }: { readonly status: AdminUserSummary['status'] }) {
  return (
    <Chip
      color={
        status === 'ACTIVE'
          ? 'success'
          : status === 'SUSPENDED'
            ? 'warning'
            : status === 'CLOSED'
              ? 'default'
              : 'info'
      }
      label={status}
      size="small"
    />
  );
}
